import { useEffect, useState } from 'react';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { Button } from './Button';
import { useCapabilities } from '../../lib/capabilities';
import { recordDiscovery } from '../../lib/api/discovery.gen';
import { createFeatureRequest } from '../../lib/api/featureRequests.gen';

// PlanTeaser (P02 §6) — the locked-feature discovery card. Reads discovery meta
// from the capability document, so a locked feature advertises itself instead of
// hiding value. Renders nothing for discovery.mode 'hidden' (internal/beta).
// Owns the discovery loop end-to-end (stories 2-1/2-2/2-6): emits the
// feature.discovery impression on mount and cta on click, and the CTA files a
// 2-2 feature request (POST /me/feature-requests) into the platform queue — so
// every teaser, in any host (modal, inline, FlagGate default), has a working
// "Request access" unless the caller overrides onRequestAccess.
export interface PlanTeaserProps {
  feature: string;
  name?: string;
  /** Override the built-in request flow (default: file a 2-2 feature request). */
  onRequestAccess?: () => void;
  className?: string;
}

// org-scoped lock (owner disabled it) vs plan-locked (entitlement) — the funnel
// dimension, best-effort from the resolved feature's reason.
const planeOf = (reason?: string): 'org' | 'plan' | null => {
  if (reason === 'plan') return 'plan';
  if (reason === 'org' || reason === 'role' || reason === 'user') return 'org';
  return null;
};

type RequestPhase = 'idle' | 'pending' | 'sent' | 'already' | 'error';

export function PlanTeaser({ feature, name, onRequestAccess, className }: PlanTeaserProps): JSX.Element | null {
  const { featureState } = useCapabilities();
  const { discovery, reason } = featureState(feature);
  const [phase, setPhase] = useState<RequestPhase>('idle');

  const hidden = discovery?.mode === 'hidden';

  // Discovery impression — every teaser render is a funnel event (P02 §5).
  // Telemetry is fire-and-forget: it never blocks the UI.
  useEffect(() => {
    if (hidden) return;
    void recordDiscovery({ featureKey: feature, action: 'impression', plane: planeOf(reason) }).catch(() => {
      /* telemetry is best-effort */
    });
  }, [feature, hidden, reason]);

  if (hidden) return null;

  const label = name ?? feature;
  const copy = discovery?.teaserCopy ?? 'This feature is available on a higher plan.';
  const isContact = discovery?.mode === 'contact';
  const cta = isContact
    ? 'Talk to us'
    : `Available on ${discovery?.upsellPlanSlug ?? 'a higher plan'} · Request access`;

  const requestAccess = async (): Promise<void> => {
    void recordDiscovery({ featureKey: feature, action: 'cta', plane: planeOf(reason) }).catch(() => {
      /* telemetry is best-effort */
    });
    if (onRequestAccess) {
      onRequestAccess();
      return;
    }
    setPhase('pending');
    try {
      const request = await createFeatureRequest({ featureKey: feature });
      // The backend is idempotent per (org, feature): a live pending request is
      // returned as-is, so an older createdAt means this org already asked.
      const already = Date.now() - new Date(request.createdAt).getTime() > 60_000;
      setPhase(already ? 'already' : 'sent');
    } catch {
      setPhase('error');
    }
  };

  return (
    <div
      className={clsx('glass-card flex flex-col gap-3 p-5 text-sm', className)}
      role="group"
      aria-label={`${label} — locked feature`}
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        <LockClosedIcon className="h-5 w-5" aria-hidden />
        {/* Story 2-147qb: converged onto the minted `tracking-eyebrow` token
            (0.3em), same eyebrow-label job as `QuotaTeaser`'s sibling span. */}
        <span className="text-xs font-semibold uppercase tracking-eyebrow">Locked</span>
      </div>
      <p className="text-base font-semibold text-foreground">{label}</p>
      <p className="text-muted-foreground">{copy}</p>
      {phase === 'sent' || phase === 'already' ? (
        <p className="text-sm font-medium text-success" role="status">
          {phase === 'already'
            ? 'Already requested — your request is pending review.'
            : 'Request sent ✓ — the platform team has been notified.'}
        </p>
      ) : (
        <div className="space-y-2">
          <Button variant="secondary" onClick={() => void requestAccess()} disabled={phase === 'pending'}>
            {cta}
          </Button>
          {phase === 'error' && (
            <p className="text-sm text-destructive" role="alert">
              Couldn’t send the request — please try again.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
